import { emptyArray } from 'helpers/empty-array';
import { CascadeLevel, CascadeSelection } from './types';

/** The levels that are visible with the given selection, from the root down.*/
export function resolveChain<TValue>(
  root: CascadeLevel<TValue>,
  selection: CascadeSelection<TValue>,
): CascadeLevel<TValue>[] {
  const chain: CascadeLevel<TValue>[] = [];

  const visited = new Set<CascadeLevel<TValue>>();

  let level: CascadeLevel<TValue> | undefined = root;
  while (level && !visited.has(level)) {
    visited.add(level);
    chain.push(level);

    level = getNextLevel(level, selection);
  }

  return chain;
}

function getNextLevel<TValue>(
  level: CascadeLevel<TValue>,
  selection: CascadeSelection<TValue>,
): CascadeLevel<TValue> | undefined {
  if (level.multiple) return undefined;

  const selected = getSingleValue(selection, level.key);
  return level.options.find((option) => option.value === selected)?.next;
}

/**
 * The selection with the values of no longer reachable levels removed, as well
 * as the values that are not among the options of their level.
 */
export function pruneSelection<TValue>(
  root: CascadeLevel<TValue>,
  selection: CascadeSelection<TValue>,
): CascadeSelection<TValue> {
  const result: CascadeSelection<TValue> = {};

  for (const level of resolveChain(root, selection)) {
    const isKnown = (value: TValue) =>
      level.options.some((option) => option.value === value);

    if (level.multiple) {
      const values = getMultipleValues(selection, level.key).filter(isKnown);
      if (values.length) {
        result[level.key] = values;
      }
      continue;
    }

    const value = getSingleValue(selection, level.key);
    if (value !== undefined && isKnown(value)) {
      result[level.key] = value;
    }
  }

  return result;
}

export function getSingleValue<TValue>(
  selection: CascadeSelection<TValue>,
  key: string,
): TValue | undefined {
  const value = selection[key];
  return Array.isArray(value) ? undefined : (value as TValue | undefined);
}

export function getMultipleValues<TValue>(
  selection: CascadeSelection<TValue>,
  key: string,
): TValue[] {
  const value = selection[key];
  if (value === undefined) return emptyArray<TValue>();

  return (Array.isArray(value) ? value : [value]) as TValue[];
}
